import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function eur(n) {
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

export default function ESGCalculator() {
  const [monthly, setMonthly] = useState(200);
  const [years, setYears] = useState(20);
  const [rate, setRate] = useState(6); // %

  const data = useMemo(() => {
    const rows = [];
    let capital = 0;
    for (let y = 1; y <= years; y++) {
      // monthly compounding for 12 months
      const monthlyRate = rate / 100 / 12;
      for (let m = 0; m < 12; m++) {
        capital = (capital + monthly) * (1 + monthlyRate);
      }
      rows.push({ year: y, capital: Math.round(capital), invested: monthly * 12 * y });
    }
    return rows;
  }, [monthly, years, rate]);

  const final = data[data.length - 1] || { capital: 0, invested: 0 };
  const gain = final.capital - final.invested;

  return (
    <div className="border border-neutral-200 bg-white p-8 md:p-10" data-testid="esg-calculator">
      <div className="overline mb-3">Rechner · ESG-ETF-Sparplan</div>
      <h3 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2">Nachhaltigkeits-Rendite-Rechner</h3>
      <p className="text-neutral-600 mb-8 max-w-2xl">
        Schätzen Sie den Endwert eines regelmäßigen Sparplans in nachhaltige ETFs.
        Historisch erzielten breite ESG-Aktienindizes zwischen 4 % und 8 % jährliche Rendite.
      </p>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-8">
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Monatliche Sparrate</label>
              <span className="font-mono text-sm text-brand-primary" data-testid="esg-monthly-value">{eur(monthly)}</span>
            </div>
            <Slider value={[monthly]} min={25} max={1500} step={25} onValueChange={(v) => setMonthly(v[0])} data-testid="esg-slider-monthly" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Anlagehorizont</label>
              <span className="font-mono text-sm text-brand-primary">{years} Jahre</span>
            </div>
            <Slider value={[years]} min={5} max={40} step={1} onValueChange={(v) => setYears(v[0])} data-testid="esg-slider-years" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Erwartete Rendite p.a.</label>
              <span className="font-mono text-sm text-brand-primary">{rate.toFixed(1)} %</span>
            </div>
            <Slider value={[rate]} min={2} max={10} step={0.5} onValueChange={(v) => setRate(v[0])} data-testid="esg-slider-rate" />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Eingezahlt</div>
              <div className="font-mono text-lg text-brand-dark" data-testid="esg-invested">{eur(final.invested)}</div>
            </div>
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Wertzuwachs</div>
              <div className="font-mono text-lg text-brand-primary" data-testid="esg-gain">{eur(gain)}</div>
            </div>
            <div className="border border-brand-primary p-4 bg-brand-primary text-white">
              <div className="text-xs uppercase tracking-wider text-white/80 mb-1">Endkapital</div>
              <div className="font-mono text-lg" data-testid="esg-final">{eur(final.capital)}</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="capitalFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3E5743" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3E5743" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => `Jahr ${l}`} />
                <Area type="monotone" dataKey="capital" stroke="#3E5743" fill="url(#capitalFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-6">
        Modellrechnung ohne Steuern und Kosten. Keine Anlageempfehlung.
      </p>
    </div>
  );
}
